import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConnectServicesActivationModal, {
  useConnectServicesActivationModal,
} from "@shared/ConnectServicesActivationModal";
import ConnectServicesSupportModal from "@shared/ConnectServicesSupportModal";
import HeroHeader from "./HeroHeader";
import NavButtons from "./NavButtons";
import CardSection from "./CardSection";
import RecruiterAiPopular from "./RecruiterAiPopular";
import RecruiterCard from "./RecruiterCard";
import OpportunitiesCard from "./OpportunitiesCard";
import PhoneSystemCard from "./PhoneSystemCard";
import BrivityLeadsCard from "./BrivityLeadsCard";
import BrivityVaCard from "./BrivityVaCard";
import ForwardCoachingCard from "./ForwardCoachingCard";
import BrivityLeadsPopular from "./BrivityLeadsPopular";
import IntegrationCard from "./shared/IntegrationCard";
import IntegrationModal from "../Layout/Sidebar/IntegrationModal";
import Testimonials from "./Testimonials";
import useHasRecruiterSubscription from "./hooks/useHasRecruiterSubscription";
import useRefreshHubSpotCta from "./hooks/useRefreshHubSpotCta";
import { setConnectServiceActive } from "../reducers/layoutReducer";
import { useAccountProducts, useIsAdmin } from "../reducers/layoutReducer/selectors";

const NAV_OPTIONS = ["Popular", "Tech", "Services", "Integrations"];

const AddOns = () => {
  const integrations = useSelector((state) =>
    state.layout.apps.filter((o) => /^zapier|zillow$/.test(o.slug)),
  );
  // Connect Services activation is admin-only. The backend already omits this app for non-admins;
  // the guard keeps the card scoped even if a stale or hand-built store carries it.
  const isAdmin = Boolean(useIsAdmin());
  const connectServiceEnabled = Boolean(useAccountProducts().connectService);
  const { isOpen, open, close } = useConnectServicesActivationModal();
  const dispatch = useDispatch();
  const connectServices = useSelector((state) =>
    state.layout.apps.find((o) => o.slug === "connect-services"),
  );
  const [openIntegration, setOpenIntegration] = useState(null);
  // Local, unlike the activation modal in Redux: that one also opens from the transaction
  // sidebar, while support is only ever reachable from this card.
  const [showSupport, setShowSupport] = useState(false);
  const [config, setConfig] = useState({
    selectedOption: "Popular",
    scroll: false,
  });
  const rootRef = useRef(null);
  const popularRef = useRef(null);
  const techRef = useRef(null);
  const servicesRef = useRef(null);
  const integrationsRef = useRef(null);
  const hasRecruiterSubscription = useHasRecruiterSubscription();

  useRefreshHubSpotCta();

  useEffect(() => {
    const refs = [
      { ref: popularRef, option: "Popular" },
      { ref: techRef, option: "Tech" },
      { ref: servicesRef, option: "Services" },
      { ref: integrationsRef, option: "Integrations" },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry with the largest intersection ratio
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible) {
          const found = refs.find((r) => r.ref.current === visible.target);
          if (found && config.selectedOption !== found.option) {
            setConfig({ selectedOption: found.option });
          }
        }
      },
      { root: rootRef.current, threshold: [0, 0.5, 1] },
    );

    refs.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [config.selectedOption]);

  useEffect(() => {
    if (!config.scroll) return;
    const verticalOffset = 100;
    const behavior = "smooth";
    if (config.selectedOption === "Popular") {
      rootRef.current.scrollTo({ top: popularRef.current.offsetTop - verticalOffset, behavior });
    }
    if (config.selectedOption === "Tech") {
      rootRef.current.scrollTo({ top: techRef.current.offsetTop - verticalOffset, behavior });
    }
    if (config.selectedOption === "Services") {
      rootRef.current.scrollTo({ top: servicesRef.current.offsetTop - verticalOffset, behavior });
    }
    if (config.selectedOption === "Integrations") {
      rootRef.current.scrollTo({ top: integrationsRef.current.offsetTop - verticalOffset, behavior });
    }
  }, [config]);

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-[64px] tw-max-h-full tw-overflow-auto tw-pb-[64px]"
      ref={rootRef}
    >
      <HeroHeader />

      <NavButtons
        options={NAV_OPTIONS}
        value={config.selectedOption}
        onChange={(selectedOption) => setConfig({ selectedOption, scroll: true })}
      />

      <CardSection title="Popular" ref={popularRef}>
        <RecruiterAiPopular hasSubscription={hasRecruiterSubscription} />
        <BrivityLeadsPopular />
      </CardSection>
      <CardSection title="Tech Enhancements" ref={techRef}>
        <RecruiterCard hasSubscription={hasRecruiterSubscription} />
        <OpportunitiesCard />
        <PhoneSystemCard />
      </CardSection>
      <CardSection title="Brivity Services" ref={servicesRef}>
        <BrivityLeadsCard />
        <BrivityVaCard />
        <ForwardCoachingCard />
      </CardSection>
      <Testimonials />
      <CardSection
        title="Integrations"
        ref={integrationsRef}
        layoutClassName="tw-grid tw-grid-cols-[repeat(auto-fit,650px)] tw-justify-center"
      >
        {isAdmin && connectServices && (
          <IntegrationCard
            integration={connectServices}
            enabled={connectServiceEnabled}
            onClick={connectServiceEnabled ? () => setShowSupport(true) : open}
          />
        )}
        {integrations.map((integration) => (
          <IntegrationCard
            integration={integration}
            key={integration.slug}
            onClick={() => setOpenIntegration(integration)}
          />
        ))}
      </CardSection>
      <IntegrationModal integration={openIntegration} onClose={() => setOpenIntegration(null)} />
      <ConnectServicesActivationModal
        show={isOpen}
        onCancel={close}
        onActivated={() => dispatch(setConnectServiceActive())}
      />
      <ConnectServicesSupportModal show={showSupport} onClose={() => setShowSupport(false)} />
    </div>
  );
};

export default AddOns;
